import React, { useEffect, useState } from 'react';
import { Loader2, Pin } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { useCanvasStore } from '@/stores/canvasStore';
import { isPinterestShortLink, pinterestBoardFromUrl } from '../../../supabase/functions/_shared/pinterest';
import type { ExtensionPanelProps } from '../registry';
import { readFunctionError } from '../jobs';
import { followPinterestImport } from './follow';
import { pinterestImportManifest } from './manifest';

const ACTION = pinterestImportManifest.actions[0];

/** Launcher panel: paste a board link, pick folder or cluster, import onto the open board. */
export function PinterestImportPanel({ open, onOpenChange }: ExtensionPanelProps) {
  const currentBoardId = useCanvasStore((s) => s.currentBoardId);
  const [url, setUrl] = useState('');
  const [intoFolder, setIntoFolder] = useState(true);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!open) setStarting(false);
  }, [open]);

  const trimmed = url.trim();
  const valid = !!pinterestBoardFromUrl(trimmed) || isPinterestShortLink(trimmed);
  const showHint = trimmed.length > 0 && !valid;

  const start = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || !currentBoardId || starting) return;
    setStarting(true);
    const { data, error } = await supabase.functions.invoke(ACTION.function, {
      body: { url: trimmed, board_id: currentBoardId, into_folder: intoFolder },
    });
    setStarting(false);
    if (error) {
      const { message } = await readFunctionError(error);
      toast.error(message);
      return;
    }
    setUrl('');
    onOpenChange(false);
    void followPinterestImport((data ?? {}) as Record<string, unknown>);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px] gap-0 overflow-hidden rounded-2xl bg-paper-raised p-0">
        <form onSubmit={start}>
          <div className="px-5 pb-3 pt-5">
            <DialogTitle className="flex items-center gap-2 text-base text-ink">
              <Pin className="h-4 w-4 text-ink-2" />
              Pinterest Import
            </DialogTitle>
            <DialogDescription className="mt-1 text-xs text-ink-3">
              Paste a board link. Its pins land on this board as images, each linking back to its pin.
            </DialogDescription>
          </div>
          <div className="space-y-4 px-5 pb-5">
            <div className="space-y-1.5">
              <Label htmlFor="pinterest-board-url" className="text-xs text-ink-2">Board link</Label>
              <Input
                id="pinterest-board-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="pinterest.com/name/board-name or pin.it/..."
                autoComplete="off"
                spellCheck={false}
                className="border-line bg-paper text-ink"
              />
              {showHint && (
                <p className="text-xs text-ink-3">
                  That is not a board link. Open the board on Pinterest and copy the address, or use Share, then Copy link.
                </p>
              )}
            </div>
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="pinterest-into-folder" className="text-sm text-ink">
                Put the pins in a folder
                <span className="block text-xs font-normal text-ink-3">
                  {intoFolder ? 'A new folder named after the board.' : 'One cluster beside what is already here.'}
                </span>
              </Label>
              <Switch id="pinterest-into-folder" checked={intoFolder} onCheckedChange={setIntoFolder} />
            </div>
            <div className="flex items-center justify-between gap-3 pt-1">
              <p className="text-xs text-ink-3">Up to 100 pins, newest first. Secret boards can't be imported.</p>
              <Button type="submit" size="sm" disabled={!valid || !currentBoardId || starting}>
                {starting && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                Import
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
